import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';
import { MessageSquare, X } from 'lucide-react';

const MessageContext = createContext();

export const useMessages = () => {
    const context = useContext(MessageContext);
    if (!context) {
        throw new Error('useMessages must be used within a MessageProvider');
    }
    return context;
};

export const MessageProvider = ({ children }) => {
    const { user } = useAuth();
    const [unreadCount, setUnreadCount] = useState(0);
    const [lastMessageId, setLastMessageId] = useState(null);
    const lastCheckTimeRef = useRef(Date.now());
    const pollingIntervalRef = useRef(null);

    const fetchUnreadSummary = async (showToast = false) => {
        if (!user) return;

        try {
            const response = await api.get('/messages/unread-summary');
            const { unreadCount: count, latestMessage } = response.data;

            setUnreadCount(count);

            if (latestMessage) {
                // Check if this is a new message we haven't seen yet
                // and it was created AFTER we started checking (to avoid notifying on old messages on load)
                // and it wasn't sent by us (handled by backend query usually, but double check)
                const msgTime = new Date(latestMessage.created_at).getTime();

                if (latestMessage.id !== lastMessageId &&
                    msgTime > lastCheckTimeRef.current &&
                    showToast) {

                    // Show notification
                    toast.custom((t) => (
                        <div
                            className={`${t.visible ? 'animate-enter' : 'animate-leave'
                                } max-w-md w-full bg-white shadow-xl rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5 border-l-4 border-primary`}
                        >
                            <div className="flex-1 w-0 p-4">
                                <div className="flex items-start">
                                    <div className="flex-shrink-0 pt-0.5">
                                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                                            <MessageSquare className="h-6 w-6 text-primary" />
                                        </div>
                                    </div>
                                    <div className="ml-3 flex-1">
                                        <p className="text-sm font-bold text-gray-900">
                                            {latestMessage.sender_name}
                                        </p>
                                        <p className="mt-1 text-sm text-gray-600 line-clamp-2 leading-relaxed">
                                            {latestMessage.content || 'Sent an attachment'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="flex border-l border-gray-100">
                                <button
                                    onClick={() => toast.dismiss(t.id)}
                                    className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-gray-400 hover:text-primary hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-primary transition-colors"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                        </div>
                    ), { position: 'bottom-right', duration: 4000 });

                    // Update check time
                    lastCheckTimeRef.current = msgTime;
                }

                setLastMessageId(latestMessage.id);
            }
        } catch (error) {
            console.error('Error fetching message summary:', error);
        }
    };

    // Initial fetch
    useEffect(() => {
        if (user) {
            // Initial fetch without toast
            fetchUnreadSummary(false);
            // Set check time to now to avoid notifying about history
            lastCheckTimeRef.current = Date.now();
        }
    }, [user]);

    // Polling effect
    useEffect(() => {
        if (user) {
            pollingIntervalRef.current = setInterval(() => {
                fetchUnreadSummary(true);
            }, 3000); // Poll every 3 seconds

            return () => {
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                }
            };
        }
    }, [user, lastMessageId]); // Dependency on lastMessageId ensures we have latest state

    const value = {
        unreadCount,
        refreshMessages: () => fetchUnreadSummary(false)
    };

    return (
        <MessageContext.Provider value={value}>
            {children}
        </MessageContext.Provider>
    );
};
